import * as approvalServiceTypes from './approvals-service-types';
import {PhotoSet} from '../types/Common'

export interface ApprovalLevelConfig extends approvalServiceTypes.ApprovalLevelConfigDTO {
    //below ones are for view model only
    visible ?: boolean,
    approvers: ApprovalApprover[];
    status: ApprovalStatus
}

export interface ApprovalUser {
    UserId: string;
    Email: string;
    GivenName: string;
    Surname: string;
    ProfilePhotoThumbnails ?: PhotoSet;
};

export interface ApprovalApprover extends ApprovalUser {
    ApproverKind: string;
    Level: number;
    // not returned from service, for UI
    Status ?: ApprovalStatus
};

export interface ApprovalProperty {
    Name: string;
    Value: string;
}

export enum ApprovalStatus {
    PENDING = "PENDING",
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
    QUEUED = "QUEUED"
}

export enum ApprovalOperationName {
    UPDATE = "UPDATE",
    PUBLISH = "PUBLISH",
    CANCEL = "CANCEL"
}

export enum ApprovalState {
    DRAFT = "DRAFT",
    PUBLISHED = "PUBLISHED",
    ACTIONED = "ACTIONED",
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
    CANCELLED = "CANCELLED"
};

export enum ApprovalActionType {
    APPROVE = "APPROVE",
    REJECT = "REJECT"
};

export enum ApprovalDetailsServiceMode {
    CREATE = "CREATE",
    EDIT = "EDIT"
};

export interface CreateApprovalRequest {
    TemplateId: string;
    Description: string;
    LevelConfigs: ApprovalLevelConfig[];
    Properties: ApprovalProperty[];
};

export interface ApprovalAction {
    CreationDate: string;
    ApprovalId: string;
    Approver: ApprovalUser;
    ApproveGroup: ApprovalUser;
    DelegateUser: ApprovalUser;
    LevelNumber: number;
    Action: ApprovalActionType;
    Comment: string;
}

export interface ApprovalBasicInfo {
    Id: string;
    Title: string;
    Description: string;
    Owner: ApprovalUser;
    CreationDate: string;
    ModificationDate: string;
    PublishDate: string;
    CompleteDate: string;
    TemplateId: string;
    MarkdownDescription ?: string;
    State: ApprovalState;
    CurrentLevel: number;
    CurrentLevelDueDate: string;
    Properties: ApprovalProperty[];
}

export interface ApprovalInfo extends ApprovalBasicInfo {
    // The following properties are not returned by the service
    LevelConfigs: ApprovalLevelConfig[];
    Resources: ApprovalResource[];
};

export interface ApprovalResource {
    ResourceUri: string;
    ResourceKind: string;
    Properties: ApprovalProperty[];
    ResourceName: string;
}

export interface ApprovalDelegateUser {
    Approver: ApprovalUser;
    Delegate: ApprovalUser;
    EffectiveDate: string;
    ExpirationDate: string;
}

export enum DescribeApprovalIncludeTypes {
    ASSIGNED = "ASSIGNED",
    TAKENACTION = "TAKENACTION",
    INVOLVED = "INVOLVED" ,
}

export enum DescribeApprovalOwnerSortTypes {
    LASTMODIFIEDDATE = "LASTMODIFIEDDATE" ,
    PUBLISHDATE = "PUBLISHDATE" ,
    COMPLETEDATE = "COMPLETEDATE" ,
    CURRENTLEVELDUEDATE = "CURRENTLEVELDUEDATE" ,
}

export interface ApprovalService {
    CreateApproval: (title: string, discription: string, LevelConfigs: ApprovalLevelConfig[]) => ng.IHttpPromise<{ApprovalInfo: ApprovalInfo}>;
    GetApproval: (approvalId: string) => ng.IHttpPromise<{ApprovalInfo: ApprovalInfo}>;
    UpdateApproval: (approvalId: string, OperationName: ApprovalOperationName, updatedApprovalRequest: any) => ng.IHttpPromise<{ApprovalInfo: ApprovalInfo}>;
    DeleteApproval: () => ng.IPromise<void>;
    DescribeApprovals: (include: DescribeApprovalIncludeTypes, sort: DescribeApprovalOwnerSortTypes, order: string, marker: string) => ng.IPromise<approvalServiceTypes.DescribeApprovalsResponse>;

    CreateApprovalResources: (approvalId: string, resources: ApprovalResource[]) => ng.IHttpPromise<approvalServiceTypes.CreateApprovalResourcesResponse>;
    DeleteApprovalResources: (approvalId: string, resources: ApprovalResource[]) => ng.IHttpPromise<approvalServiceTypes.DeleteApprovalResourcesResponse>;
    DescribeApprovalResources: (approvalId: string, marker: string, delegateApproverId) => ng.IHttpPromise<approvalServiceTypes.DescribeApprovalResourcesResponse>;
    
    CreateApprovers: (approvalId: string, approvers: ApprovalApprover[]) => ng.IHttpPromise<approvalServiceTypes.CreateApproversResponse>;
    DeleteApprovers: (approvalId: string, approvers: ApprovalApprover[]) => ng.IHttpPromise<approvalServiceTypes.DeleteApproversResponse>;
    DescribeApprovers: (approvalId: string, marker: string, delegateApproverId: string) => ng.IHttpPromise<approvalServiceTypes.DescribeApproversResponse>;
    DescribeAllApprovers: (approvalId: string, delegateApproverId: string) => ng.IPromise<ApprovalApprover[]>;

    CreateApprovalAction: (approval: ApprovalInfo, action: ApprovalActionType, actionComment: string, delegateApproverId: string) => ng.IHttpPromise<approvalServiceTypes.DescribeApprovalActionsResponse>;
    DescribeApprovalActions: (approvalId: string, marker: string, delegateApproverId: string) => ng.IHttpPromise<approvalServiceTypes.DescribeApprovalActionsResponse>;
    DescribeAllApprovalActions: (approvalId: string, delegateApproverId: string) => ng.IPromise<ApprovalAction[]>

    CreateApprovalDelegate: () => ng.IPromise<void>;
    DeleteApprovalDelegate: () => ng.IPromise<void>;
    DescribeApprovalDelegates: () => ng.IPromise<approvalServiceTypes.DescribeApprovalDelegatesResponse>;
}

export interface ApprovalDetailsService {
    initService: (approvalRequest?: ApprovalInfo, storeMode?: ApprovalDetailsServiceMode) => void;
    getServiceMode: () => ApprovalDetailsServiceMode;
    isPanelVisiable: () => boolean;
    setPanelVisibility: (visibility: boolean) => boolean;
    getApproval: () => ApprovalBasicInfo;
    setApproval: (approval: any) => any;
    getApprovalResources: () => ApprovalResource[];
    appendApprovalResource: (approvalResource: ApprovalResource) => ApprovalResource[];
    deleteApprovalResourceByUri: (uri: string) => ApprovalResource[];
    getApprovalApprovers: () => ApprovalApprover[];
    getApprovalApproversByLevelNumber: (levelNum: number) => ApprovalApprover[];
    appendApprovalApproverLevel: (approvers: ApprovalApprover[]) => ApprovalApprover[][];
    appendApprovalApproverToLevel: (levelNum: number, approver: ApprovalApprover) => ApprovalApprover[][];
    deleteApprovalApproverLevelByLevelNumber: (levelNum: number) => ApprovalApprover[][];
    deleteApprovalApproverByUserId: (id: string) => ApprovalApprover[][];
    getApprovalLevelConfigs: () => ApprovalLevelConfig[];
    setApprovalLevelConfigs: (levelConfigs: ApprovalLevelConfig[]) => ApprovalLevelConfig[];
    appendApprovalLevelConfig: (levelConfig: ApprovalLevelConfig) => ApprovalLevelConfig[];
    deleteApprovalLevelConfigByLevelNumber: (levelNum: number) => ApprovalLevelConfig[];
    getPreviousRequestId: () => string;
    isResourceModificationAllowed: () => boolean;
    isApprovalRequestChanged: () => boolean;
    getApprovalRequestDTO: () => approvalServiceTypes.ApprovalRequestDTO;
    getAddedApprovers: () => ApprovalApprover[];
    getRemovedApprovers: () => ApprovalApprover[];
    getAddedResources: () => ApprovalResource[];
    getRemovedResources: () => ApprovalResource[];
}